import { useEffect } from "react";
import { X } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useUpdateOffer } from "../hooks/useOfferMutations";
import { offerEditSchema } from "../validations/offerEditForm";

export const EditOfferModal = ({ isEditModalOpen, setIsEditModalOpen, offer }) => {
    const { mutate: updateOffer, isPending } = useUpdateOffer();

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        resolver: zodResolver(offerEditSchema),
        defaultValues: {
            title: "",
            code: "",
            discountType: "fixed",
            status: "pending",
            discountValue: 0,
            minOrderValue: 0,
            maxDiscountValue: 0,
            startDate: "",
            endDate: "",
            conditions: "",
            autoApply: false,
            isDraft: false,
            
        },
    });

    /* Load Data */
    useEffect(() => {
        if (offer) {
            reset({
                title: offer.title || "",
                code: offer.code || "",
                discountType: offer.discountType || "fixed",
                status: offer.status || "pending",
                discountValue: offer.discountValue || 0,
                minOrderValue: offer.minOrderValue || 0,
                maxDiscountValue: offer.maxDiscountValue || 0,
                startDate: offer.startDate?.substring(0, 16) || "",
                endDate: offer.endDate?.substring(0, 16) || "",
                conditions: offer.conditions || "",
                autoApply: offer.autoApply || false,
                isDraft: offer.isDraft || false,
            });
        }
    }, [offer, reset]);

    const onSubmit = (data) => {
        updateOffer(
            {
                offerId: offer._id,
                offerData: data,
            },
            {
                onSuccess: () => {
                    reset();
                    setIsEditModalOpen(false);
                },
            }
        );
    };

    if (!isEditModalOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
            <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto custom-scrollbar">

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-border">
                    <h3 className="text-lg font-semibold">Edit Offer</h3>
                    <button onClick={() => setIsEditModalOpen(false)} className="text-gray-400 hover:text-black">
                        <X size={20} />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                        {/* Title */}
                        <Field label="Title" error={errors.title?.message}>
                            <input
                                {...register("title")}
                                className="input w-full"
                                placeholder="Offer Title"
                            />
                        </Field>

                        {/* Code */}
                        <Field label="Code" error={errors.code?.message}>
                            <input
                                {...register("code")}
                                className="input w-full"
                                placeholder="Unique Code"
                                onKeyDown={(e) => {
                                    if (e.key === " ") {
                                        e.preventDefault();
                                    }
                                }}
                                onInput={(e) => {
                                    e.target.value = e.target.value
                                        .replace(/\s/g, "") // Remove spaces
                                        .toUpperCase();     // Convert to uppercase
                                }}
                            />
                        </Field>

                        <Field label="Status" error={errors.status?.message}>
                            <select {...register("status")} className="input w-full">
                                <option value="pending">Pending</option>
                                <option value="active">Active</option>
                                <option value="expired">Expired</option>
                                <option value="scheduled">Scheduled</option>
                            </select>
                        </Field>

                        {/* Discount Type */}
                        <Field label="Discount Type" error={errors.discountType?.message}>
                            <select {...register("discountType")} className="input w-full">
                                <option value="percentage">Percentage</option>
                                <option value="fixed">Fixed</option>
                            </select>
                        </Field>

                        {/* Discount Value */}
                        <Field label="Discount Value" error={errors.discountValue?.message}>
                            <input
                                type="number"
                                {...register("discountValue", { valueAsNumber: true })}
                                className="input w-full"
                                min="0"
                            />
                        </Field>

                        {/* Min Order Value */}
                        <Field label="Min Order Value" error={errors.minOrderValue?.message}>
                            <input
                                type="number"
                                {...register("minOrderValue", { valueAsNumber: true })}
                                className="input w-full"
                                min="0"
                            />
                        </Field>

                        {/* Max Discount Value */}
                        <Field label="Max Discount Value" error={errors.maxDiscountValue?.message}>
                            <input
                                type="number"
                                {...register("maxDiscountValue", { valueAsNumber: true })}
                                className="input w-full"
                                min="0"
                            />
                        </Field>

                        {/* Start Date */}
                        <Field label="Start Date" error={errors.startDate?.message}>
                            <input
                                type="datetime-local"
                                {...register("startDate")}
                                className="input w-full"
                            />
                        </Field>

                        {/* End Date */}
                        <Field label="End Date" error={errors.endDate?.message}>
                            <input
                                type="datetime-local"
                                {...register("endDate")}
                                className="input w-full"
                            />
                        </Field>

                        {/* Auto Apply */}
                        {/* <Field label="Auto Apply" error={errors.autoApply?.message}>
                            <input type="checkbox" {...register("autoApply")} className="h-4 w-4" />
                        </Field> */}

                        {/* Draft */}
                        {/* <Field label="Draft" error={errors.isDraft?.message}>
                            <input type="checkbox" {...register("isDraft")} className="h-4 w-4" />
                        </Field> */}
                    </div>

                    {/* Description(initially as conditions) */}
                    <Field label="Description" error={errors.description?.message}>
                        <textarea
                            {...register("description")}
                            className="input min-h-[80px] w-full"
                            placeholder="Write description here..."
                        />
                    </Field>

                    {/* Submit */}
                    <div className="flex justify-end">
                        <button
                            type="submit"
                            disabled={isPending}
                            className="bg-primary text-white font-semibold px-4 py-2 rounded-md disabled:opacity-50"
                        >
                            {isPending ? "Updating..." : "Update Offer"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

/* Reusable Field Component */
const Field = ({ label, error, children }) => (
    <div>
        <label className="block text-sm mb-1">{label}</label>
        {children}
        {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
);

/* Global input class */
const inputClass = `
    w-full bg-gray-800 border border-gray-700 text-white rounded-md px-3 py-2
`;

export default EditOfferModal;